import React from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { CssBaseline, Box } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';

// Context e Componentes de Auth
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';

// Components
import AppShell from './components/AppShell';

// Pages - Public
import Login from './pages/Login';
import Signup from './pages/Signup';
import ResetPassword from './pages/ResetPassword';
import EmailConfirmation from './pages/EmailConfirmation'; // ✅ Nova importação
import Landing from './pages/Landing';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';

// Pages - Protected
import CreatePost from './pages/CreatePost';
import CreateStory from './pages/CreateStory';
import CreateReels from './pages/CreateReels';
import CreateBrandImage from './pages/CreateBrandImage';
import Callback from './pages/Callback';
import StoryCalendar from './pages/StoryCalendar';
import EditStory from './pages/EditStory';
import EditPost from './pages/EditPost';
import HomePage from './pages/HomePage';
import ClientDashboard from './pages/ClientDashboard';
import SingleClientDashboard from './pages/SingleClientDashboard';
import Settings from './pages/Settings';
import AdminSettings from './pages/AdminSettings';
import SuperAdminLogin from './pages/SuperAdminLogin';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import Checkout from './pages/Checkout';
import CheckoutSuccess from './pages/CheckoutSuccess';
import CheckoutCancel from './pages/CheckoutCancel';
import ClientDashboardView from './pages/ClientDashboardView';
import ClientApprovalView from './pages/ClientApprovalView';
import ManagerInternalApprovalView from './pages/ManagerInternalApprovalView';
import ShareLinksPage from './pages/ShareLinksPage';
import ApprovalsPage from './pages/ApprovalsPage';
import PlansPage from './pages/PlansPage';
import ConnectInstagramBusiness from './pages/ConnectInstagramBusiness';
import InstagramBusinessCallback from './pages/InstagramBusinessCallback';
import InstagramBusinessDemo from './pages/InstagramBusinessDemo';

// Layout compartilhado para todas as páginas protegidas
import ApprovalOnlyGuard from './components/ApprovalOnlyGuard';
import { ApprovalOnlyTourProvider } from './contexts/ApprovalOnlyTourContext';

const PageLayout = ({ children }: { children: React.ReactNode }) => (
  <ApprovalOnlyTourProvider>
    <AppShell>
      <ApprovalOnlyGuard>{children}</ApprovalOnlyGuard>
    </AppShell>
  </ApprovalOnlyTourProvider>
);

// Layout sem cabeçalho para páginas de callback
const CallbackLayout = ({ children }: { children: React.ReactNode }) => (
  <Box 
    sx={{ 
      minHeight: '100vh', 
      bgcolor: 'background.default', 
      display: 'flex', 
      flexDirection: 'column',
      color: 'text.primary'
    }}
  >
    {children}
  </Box>
);

// Layout para páginas públicas (login, etc)
const PublicLayout = ({ children }: { children: React.ReactNode }) => (
  <Box 
    sx={{ 
      minHeight: '100vh',
      bgcolor: 'background.default',
      display: 'flex',
      flexDirection: 'column'
    }}
  >
    {children}
  </Box>
);

const MarketingLayout = ({ children }: { children: React.ReactNode }) => (
  <Box
    className="marketing-shell"
    sx={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    {children}
  </Box>
);

// Componente para verificar se o usuário está logado antes de renderizar rotas protegidas
const ProtectedPageLayout = ({ children }: { children: React.ReactNode }) => (
  <ProtectedRoute>
    <PageLayout>
      {children}
    </PageLayout>
  </ProtectedRoute>
);

// Componente para páginas que requerem role de admin
const AdminPageLayout = ({ children }: { children: React.ReactNode }) => (
  <ProtectedRoute requiredRole="admin">
    <PageLayout>
      {children}
    </PageLayout>
  </ProtectedRoute>
);

// Componente para páginas que requerem role de super_admin (sem header)
const SuperAdminPageLayout = ({ children }: { children: React.ReactNode }) => (
  <ProtectedRoute requiredRole="super_admin" redirectTo="/super-admin/login">
    <CallbackLayout>
      {children}
    </CallbackLayout>
  </ProtectedRoute>
);

// Componente para callback público (popup OAuth não deve exigir novo login)
const PublicCallbackLayout = ({ children }: { children: React.ReactNode }) => (
  <CallbackLayout>
    {children}
  </CallbackLayout>
);

// Configuração do router com autenticação
const router = createBrowserRouter([
  // Rotas públicas
  {
    path: "/landing",
    element: <MarketingLayout><Landing /></MarketingLayout>,
  },
  {
    path: "/login",
    element: <PublicLayout><Login /></PublicLayout>,
  },
  {
    path: "/signup",
    element: <PublicLayout><Signup /></PublicLayout>,
  },
  {
    path: "/reset-password",
    element: <PublicLayout><ResetPassword /></PublicLayout>,
  },
  {
    path: "/email-confirmation", // ✅ Nova rota para confirmação de email
    element: <PublicLayout><EmailConfirmation /></PublicLayout>,
  },
  {
    path: "/super-admin/login",
    element: <PublicLayout><SuperAdminLogin /></PublicLayout>,
  },
  {
    // Canonical privacy URL we share with Meta / Instagram (App Review,
    // Deauthorize callback footer, etc.). `/privacy-policy` is kept as a
    // legacy alias so existing links stay valid.
    path: "/privacy",
    element: <PublicLayout><PrivacyPolicy /></PublicLayout>,
  },
  {
    path: "/privacy-policy",
    element: <PublicLayout><PrivacyPolicy /></PublicLayout>,
  },
  {
    // Canonical terms URL we share with Meta App Review.
    path: "/terms",
    element: <PublicLayout><TermsOfService /></PublicLayout>,
  },
  {
    path: "/view/:token",
    element: <PublicLayout><ClientDashboardView /></PublicLayout>,
  },
  {
    path: "/approve/:token",
    element: <PublicLayout><ClientApprovalView /></PublicLayout>,
  },
  {
    path: "/revisao-interna/:token",
    element: <PublicLayout><ManagerInternalApprovalView /></PublicLayout>,
  },

  // Rotas protegidas - Home e Dashboard
  {
    path: "/",
    element: <ProtectedPageLayout><HomePage /></ProtectedPageLayout>,
  },
  {
    path: "/clients",
    element: <ProtectedPageLayout><ClientDashboard /></ProtectedPageLayout>,
  },
  {
    path: "/client/:clientId",
    element: <ProtectedPageLayout><SingleClientDashboard /></ProtectedPageLayout>,
  },
  
  // Rotas protegidas - Calendário
  {
    path: "/calendar",
    element: <ProtectedPageLayout><StoryCalendar /></ProtectedPageLayout>,
  },
  {
    path: "/share-links",
    element: <ProtectedPageLayout><ShareLinksPage /></ProtectedPageLayout>,
  },
  {
    path: "/approvals",
    element: <ProtectedPageLayout><ApprovalsPage /></ProtectedPageLayout>,
  },
  {
    path: "/calendar/:clientId",
    element: <ProtectedPageLayout><StoryCalendar /></ProtectedPageLayout>,
  },
  
  // Rotas protegidas - Criação de conteúdo
  {
    path: "/create-post",
    element: <ProtectedPageLayout><CreatePost /></ProtectedPageLayout>,
  },
  {
    path: "/create-story",
    element: <ProtectedPageLayout><CreateStory /></ProtectedPageLayout>,
  },
  {
    path: "/create-reels",
    element: <ProtectedPageLayout><CreateReels /></ProtectedPageLayout>,
  },
  {
    path: "/create-brand-image",
    element: <ProtectedPageLayout><CreateBrandImage /></ProtectedPageLayout>,
  },
  {
    path: "/edit-story/:id",
    element: <ProtectedPageLayout><EditStory /></ProtectedPageLayout>,
  },
  {
    path: "/edit-post/:postId",
    element: <ProtectedPageLayout><EditPost /></ProtectedPageLayout>,
  },
  {
    path: "/clients/:clientId/edit-post/:postId",
    element: <ProtectedPageLayout><EditPost /></ProtectedPageLayout>,
  },
  
  // Rotas protegidas - Configurações
  {
    path: "/settings",
    element: <ProtectedPageLayout><Settings /></ProtectedPageLayout>,
  },

  // Rota protegida - Planos (para usuários logados verem/trocarem planos)
  {
    path: "/plans",
    element: <ProtectedPageLayout><PlansPage /></ProtectedPageLayout>,
  },
  
  // Rota administrativa - APENAS ADMINS
  {
    path: "/admin",
    element: <AdminPageLayout><AdminSettings /></AdminPageLayout>,
  },
  
  // Rotas Super Admin - APENAS SUPER_ADMINS
  {
    path: "/super-admin",
    element: <SuperAdminPageLayout><SuperAdminDashboard /></SuperAdminPageLayout>,
  },
  
  // Rotas protegidas - Callbacks do Instagram
  {
    path: "/api/instagram-auth/callback",
    element: <PublicCallbackLayout><Callback /></PublicCallbackLayout>,
  },
  {
    path: "/callback",
    element: <PublicCallbackLayout><Callback /></PublicCallbackLayout>,
  },

  // Instagram Business Login — App Review demo flow (public, English UI).
  // Kept isolated from the legacy Facebook Login flow on purpose.
  {
    path: "/connect/instagram-business",
    element: <PublicLayout><ConnectInstagramBusiness /></PublicLayout>,
  },
  {
    path: "/callback/instagram-business",
    element: <PublicCallbackLayout><InstagramBusinessCallback /></PublicCallbackLayout>,
  },
  {
    path: "/connect/instagram-business/demo",
    element: <PublicLayout><InstagramBusinessDemo /></PublicLayout>,
  },
  // Authenticated flow: connect a specific client via Instagram Business Login.
  // Re-uses the same English landing + callback, but the callback detects the
  // client id stored in sessionStorage and persists the token onto the client
  // row so the n8n scheduler pipeline can publish posts for it.
  {
    path: "/clients/:clientId/connect-instagram-business",
    element: <ProtectedPageLayout><ConnectInstagramBusiness /></ProtectedPageLayout>,
  },
  
  // Rotas protegidas - Checkout Stripe
  {
    path: "/checkout",
    element: <ProtectedPageLayout><Checkout /></ProtectedPageLayout>,
  },
  {
    path: "/checkout/success",
    element: <ProtectedPageLayout><CheckoutSuccess /></ProtectedPageLayout>,
  },
  {
    path: "/checkout/cancel",
    element: <ProtectedPageLayout><CheckoutCancel /></ProtectedPageLayout>,
  },
  
  // Rota catch-all - redireciona para dashboard se logado, senão para login
  {
    path: "*",
    element: <Navigate to="/" replace />,
  },
]);

// Componente principal da aplicação
function App() {
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
      <CssBaseline />
      <AuthProvider>
        <RouterProvider router={router} />
      </AuthProvider>
    </LocalizationProvider>
  );
}

export default App;